//----------------------------------------------------
// src/pages/RoleManagement.jsx — Admin Rolbeheer (RBAC v2)
//----------------------------------------------------
import React, { useCallback, useEffect, useState } from "react";
import { getUsersByTenant, updateUserRole } from "../services/userService";
import { useUser } from "../context/UserContext";

// 🆕 Helpmodal component
function RoleHelpModal() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="text-sm text-blue-600 hover:underline"
      >
        ❓ Wat betekenen de rollen?
      </button>

      {open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-96 relative">
            <h2 className="text-xl font-bold mb-3 text-gray-800">
              Rollen & Rechten in Logboek24
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              Hieronder vind je een korte uitleg van wat elke rol mag binnen het systeem.
            </p>
            <ul className="list-disc pl-5 space-y-2 text-sm text-gray-700">
              <li><b>👑 Admin:</b> volledige toegang tot alle functies.</li>
              <li><b>📋 Projectmanager:</b> projecten en teams beheren.</li>
              <li><b>🧭 Projectleider:</b> toezicht, werkbonnen accorderen.</li>
              <li><b>🧰 Werkvoorbereider:</b> materialen voorbereiden.</li>
              <li><b>🔧 Monteur:</b> werkbonnen uitvoeren, foto’s uploaden.</li>
              <li><b>👀 Demo:</b> alleen-lezen omgeving.</li>
            </ul>
            <button
              onClick={() => setOpen(false)}
              className="mt-5 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 w-full"
            >
              Sluiten
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default function RoleManagement() {
  const { user } = useUser();  // ✔ Nieuwe RBAC context
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [melding, setMelding] = useState("");

  const laadGebruikers = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const data = await getUsersByTenant(user.tenantId, token);
      setUsers(data);
    } catch (err) {
      console.error("❌ Fout bij ophalen gebruikers:", err);
      setMelding("❌ Kon gebruikers niet ophalen.");
    } finally {
      setLoading(false);
    }
  }, [user?.tenantId]);

  useEffect(() => {
    if (user?.tenantId) laadGebruikers();
  }, [user?.tenantId, laadGebruikers]);

  async function wijzigRol(userId, nieuweRol, oudeRol) {
    const bevestig = window.confirm(
      `Weet je zeker dat je deze gebruiker wilt wijzigen van '${oudeRol}' naar '${nieuweRol}'?\nDeze wijziging wordt direct opgeslagen.`
    );
    if (!bevestig) return;

    try {
      const token = localStorage.getItem("token");
      await updateUserRole(userId, nieuweRol, token);
      setMelding(`✅ Rol gewijzigd naar ${nieuweRol}`);
      laadGebruikers();
      setTimeout(() => setMelding(""), 3000);
    } catch (err) {
      console.error("❌ Fout bij wijzigen rol:", err);
      setMelding("❌ Er ging iets mis bij het wijzigen van de rol.");
    }
  }

  if (user?.rol !== "admin") {
    return (
      <div className="p-6 text-red-600">
        🚫 Alleen admins hebben toegang tot deze pagina.
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* 🔹 Waarschuwing + helpknop */}
      <div className="flex justify-between items-start">
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded w-full mr-4">
          <p className="text-yellow-700 text-sm">
            ⚠️ <strong>Let op:</strong> Rollenbeheer is een kritische functie.
            Wijzigingen worden direct toegepast.
          </p>
        </div>
        <RoleHelpModal />
      </div>

      <h1 className="text-2xl font-bold">👥 Rollenbeheer</h1>

      {melding && <div className="text-green-600">{melding}</div>}

      {loading ? (
        <p>Laden...</p>
      ) : (
        <table className="min-w-full border border-gray-300 rounded-lg shadow-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="p-2 border">Naam</th>
              <th className="p-2 border">E-mail</th>
              <th className="p-2 border">Rol</th>
              <th className="p-2 border text-center">Wijzig</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u._id} className="hover:bg-gray-50">
                <td className="p-2 border">{u.naam}</td>
                <td className="p-2 border">{u.email}</td>
                <td className="p-2 border text-center">{u.rol}</td>
                <td className="p-2 border text-center">
                  <select
                    value={u.rol}
                    onChange={(e) => wijzigRol(u._id, e.target.value, u.rol)}
                    className="border rounded px-2 py-1"
                  >
                    <option value="admin">admin</option>
                    <option value="projectmanager">projectmanager</option>
                    <option value="projectleider">projectleider</option>
                    <option value="werkvoorbereider">werkvoorbereider</option>
                    <option value="monteur">monteur</option>
                    <option value="demo">demo</option>
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
